import React, { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useT } from '../../i18n/LocaleContext';
import { classNames } from '../../utils/format';

// Only http(s)/mailto survive the server-side jsoup safelist.
function normalizeUrl(raw) {
  const value = raw.trim();
  if (!value) return '';
  if (/^(https?:|mailto:)/i.test(value)) return value;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return `mailto:${value}`;
  return `https://${value}`;
}

function ToolButton({ label, title, active, disabled, onClick, className }) {
  return (
    <button
      type="button"
      className={classNames('rte-btn', active && 'is-active', className)}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      // Keep the editor selection while clicking toolbar buttons.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * TipTap-based editor for minutes and reports. Produces plain semantic HTML
 * (no inline styles) because the backend sanitises with a jsoup safelist.
 */
export default function RichTextEditor({ value, onChange, placeholder, invalid, id }) {
  const t = useT();
  const linkInputRef = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        // target/rel are stripped by the server safelist anyway.
        HTMLAttributes: { target: null, rel: null },
      }),
      Placeholder.configure({ placeholder: placeholder || '' }),
    ],
    content: value || '',
    editorProps: {
      attributes: { class: 'rte-content doc-content', ...(id ? { id } : {}) },
    },
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.isEmpty ? '' : ed.getHTML());
    },
  });

  // Keep in sync when the parent swaps the document being edited.
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [editor, value]);

  useEffect(() => {
    if (linkOpen) linkInputRef.current?.focus();
  }, [linkOpen]);

  if (!editor) return null;

  const run = (fn) => () => fn(editor.chain().focus()).run();

  const openLink = () => {
    setLinkUrl(editor.getAttributes('link').href || '');
    setLinkOpen(true);
  };

  const applyLink = (e) => {
    e?.preventDefault?.();
    const href = normalizeUrl(linkUrl);
    const chain = editor.chain().focus().extendMarkRange('link');
    if (href) chain.setLink({ href }).run();
    else chain.unsetLink().run();
    setLinkOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  return (
    <div className={classNames('rte', invalid && 'is-invalid')}>
      <div className="rte-toolbar" role="toolbar" aria-label={t('editor.toolbar')}>
        <div className="rte-group">
          <ToolButton label={<strong>B</strong>} title={t('editor.bold')} active={editor.isActive('bold')} onClick={run((c) => c.toggleBold())} />
          <ToolButton label={<em>I</em>} title={t('editor.italic')} active={editor.isActive('italic')} onClick={run((c) => c.toggleItalic())} />
          <ToolButton label={<u>U</u>} title={t('editor.underline')} active={editor.isActive('underline')} onClick={run((c) => c.toggleUnderline())} />
          <ToolButton label={<s>S</s>} title={t('editor.strike')} active={editor.isActive('strike')} onClick={run((c) => c.toggleStrike())} />
        </div>
        <div className="rte-group">
          {[1, 2, 3].map((level) => (
            <ToolButton
              key={level}
              label={`H${level}`}
              title={t('editor.heading', { level })}
              active={editor.isActive('heading', { level })}
              onClick={run((c) => c.toggleHeading({ level }))}
            />
          ))}
        </div>
        <div className="rte-group">
          <ToolButton label="•≡" title={t('editor.bullet_list')} active={editor.isActive('bulletList')} onClick={run((c) => c.toggleBulletList())} />
          <ToolButton label="1≡" title={t('editor.ordered_list')} active={editor.isActive('orderedList')} onClick={run((c) => c.toggleOrderedList())} />
          <ToolButton label="❝" title={t('editor.blockquote')} active={editor.isActive('blockquote')} onClick={run((c) => c.toggleBlockquote())} />
          <ToolButton label="―" title={t('editor.horizontal_rule')} onClick={run((c) => c.setHorizontalRule())} />
          <ToolButton label="🔗" title={t('editor.link')} active={editor.isActive('link') || linkOpen} onClick={openLink} />
        </div>
        <div className="rte-group rte-group--end">
          <ToolButton label="↶" title={t('editor.undo')} disabled={!editor.can().undo()} onClick={run((c) => c.undo())} />
          <ToolButton label="↷" title={t('editor.redo')} disabled={!editor.can().redo()} onClick={run((c) => c.redo())} />
        </div>
      </div>

      {linkOpen && (
        <div className="rte-linkbar">
          <input
            ref={linkInputRef}
            className="form-control"
            type="url"
            placeholder="https://"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyLink(e);
              if (e.key === 'Escape') { e.stopPropagation(); setLinkOpen(false); }
            }}
          />
          <button type="button" className="action-btn" onClick={applyLink}>{t('common.apply')}</button>
          {editor.isActive('link') && (
            <button type="button" className="action-btn danger" onClick={removeLink}>{t('editor.remove_link')}</button>
          )}
          <button type="button" className="action-btn" onClick={() => setLinkOpen(false)}>{t('common.cancel')}</button>
        </div>
      )}

      <EditorContent editor={editor} className="rte-surface" />
    </div>
  );
}
